import { gql, useQuery } from '@apollo/client'
import { useEffect, useMemo } from 'react'

import {
  ItemDetailBoxFragmentDoc,
  ItemType,
  UserItemDetailBoxFragmentDoc,
  UserItemDetailForGuestQuery,
  UserItemDetailForGuestQueryVariables,
  UserItemDetailQuery,
  UserItemDetailQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useLoginModal, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { convertPortfolioItemToSku, setItemSku } from '@/core/sku'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } =
    useRouter<{ userId: string; itemId: string; skuId?: string }>()
  const { user: currentUser } = useCurrentUser()
  const { showModal } = useLoginModal()

  const { data, loading } = useQuery<
    UserItemDetailQuery,
    UserItemDetailQueryVariables
  >(QUERY, {
    variables: {
      itemId: router.query.itemId,
      userId: router.query.userId,
      uid: router.query.userId,
    },
    skip: !currentUser?.isSigned,
  })

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    UserItemDetailForGuestQuery,
    UserItemDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: { id: router.query.itemId, userId: router.query.userId },
    skip: currentUser?.isSigned,
  })

  const hasItem = !!useQuery(HAS_ITEM_QUERY, {
    variables: { itemId: router.query.itemId, userId: currentUser?.uid },
    skip: !currentUser?.isSigned,
  })?.data

  useEffect(() => {
    if (!currentUser) {
      showModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  const tradableSerials = useMemo(() => {
    return data?.portfolio?.serials?.filter(serial => !serial.isProtected)
  }, [data?.portfolio?.serials])

  return {
    state: {
      loading: loading || loadingForGuest,
      isLoggedIn: !!currentUser,
      hasItem,
      hasNextPage: false,
      disabledTrade: !!(
        data?.portfolio?.isUserProtected ||
        data?.portfolio?.isSystemProtected ||
        // 物販アイテムの場合、所持シリアルがすべてプロテクトされている場合トレード不可
        (data?.portfolio?.item.type === ItemType.Merch &&
          tradableSerials?.length === 0) ||
        // SKUを持っているアイテムはトレードできない
        !!data?.portfolio?.item.sku
      ),
      posessionNumber:
        convertPortfolioItemToSku(data?.portfolio ?? null, router.query.skuId)
          ?.number ?? 0,
      item: setItemSku(
        data?.portfolio?.item ?? dataForGuest?.item ?? null,
        router.query.skuId
      ),
      userId: router.query.userId,
      itemId: router.query.itemId,
      user: data?.user ?? dataForGuest?.user,
    },
    actions: {},
  }
})

const QUERY = gql`
  query UserItemDetail(
    $itemId: ID!
    $userId: String
    $uid: ID!
    $skuId: String
  ) {
    portfolio(id: $itemId, userId: $userId) {
      ...UserItemDetailBox
    }
    user(id: $uid) {
      id
      avatar {
        uri
        smallUri
      }
    }
    userLoggedIn: user {
      id
    }
  }
  ${UserItemDetailBoxFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query UserItemDetailForGuest($id: ID!, $userId: ID!, $skuId: String) {
    item(id: $id) {
      ...ItemDetailBox
    }
    user(id: $userId) {
      id
      avatar {
        uri
        smallUri
      }
    }
  }
  ${ItemDetailBoxFragmentDoc}
`

const HAS_ITEM_QUERY = gql`
  query HasItemUserItemDetail($itemId: ID!, $userId: String) {
    portfolio(id: $itemId, userId: $userId) {
      id
      serials {
        serial {
          skuId
        }
      }
    }
  }
`
